/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useState } from "react";
import AppShell from "../../components/layout/AppShell";
import { toast } from "sonner";
import axios from "axios";
import {
  GraduationCap, BookOpen, Video, Exam, Certificate, Handshake, ChartBar,
  Plus, Trash, PencilSimple, CheckCircle, X, FloppyDisk, Globe, ArrowRight,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLUE = "#0B2B66";
const GOLD = "#C9A227";

const api = axios.create({ baseURL: API });
api.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("token");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
const fmtErr = (e) => {
  const d = e?.response?.data?.detail;
  if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
  if (d && typeof d === "object") return d.msg || JSON.stringify(d);
  return d || e?.message || "Something went wrong";
};

// ============================================================================
// Root
// ============================================================================
const TABS = [
  { key: "categories", label: "Categories", icon: BookOpen },
  { key: "courses",    label: "Courses", icon: GraduationCap },
  { key: "modules",    label: "Modules & Videos", icon: Video },
  { key: "exams",      label: "Exam Bank", icon: Exam },
  { key: "design",     label: "Certificate Design", icon: Certificate },
  { key: "partners",   label: "Partners", icon: Handshake },
  { key: "progress",   label: "Progress & Certs", icon: ChartBar },
];

export default function TrainingAdmin() {
  const [tab, setTab] = useState("categories");
  return (
    <AppShell>
      <div className="p-6 md:p-8 space-y-6" data-testid="training-admin">
        <header className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>LMS Configuration</div>
            <h1 className="font-heading text-3xl mt-1" style={{ color: BLUE }}>Training Academy</h1>
            <p className="text-sm text-[#5C5A55] mt-1">Create courses, modules, videos, exams, and manage certificate design + partners.</p>
          </div>
          <a href="/training" target="_blank" rel="noreferrer" data-testid="ta-open-public"
             className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#0B2B66]/20 text-xs hover:bg-[#0B2B66]/5">
            <Globe size={13}/> View public academy <ArrowRight size={11}/>
          </a>
        </header>

        {/* Tabs */}
        <div className="flex flex-wrap gap-1 border-b border-[#0B2B66]/10">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ta-tab-${t.key}`}
                    className={`h-11 px-4 inline-flex items-center gap-2 text-sm font-medium border-b-2 -mb-px transition ${
                      tab === t.key ? "border-[#0B2B66] text-[#0B2B66]" : "border-transparent text-[#5C5A55] hover:text-[#0B2B66]"
                    }`}>
              <t.icon size={14} weight={tab === t.key ? "fill" : "regular"}/> {t.label}
            </button>
          ))}
        </div>

        <div>
          {tab === "categories" && <CategoriesTab/>}
          {tab === "courses"    && <CoursesTab/>}
          {tab === "modules"    && <ModulesTab/>}
          {tab === "exams"      && <ExamsTab/>}
          {tab === "design"     && <DesignTab/>}
          {tab === "partners"   && <PartnersTab/>}
          {tab === "progress"   && <ProgressTab/>}
        </div>
      </div>
    </AppShell>
  );
}

// ============================================================================
// CATEGORIES
// ============================================================================
function CategoriesTab() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try { const r = await api.get("/admin/training/categories"); setRows(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/training/categories/${data.id}`, data);
      else         await api.post("/admin/training/categories", data);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this category? (Must have zero courses.)")) return;
    try { await api.delete(`/admin/training/categories/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Section title="Categories" sub="Group courses by audience: customer / staff / official / govt.">
      <Btn onClick={() => setEditing({ name: "", audience: "customer", order: rows.length + 1, is_active: true })} testid="ta-cat-new">
        <Plus size={12}/> New category
      </Btn>
      <Table cols={["Name", "Audience", "Order", "Status", ""]}>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-[#0B2B66]/5 hover:bg-[#0B2B66]/2" data-testid={`cat-${r.id}`}>
            <td className="p-3"><div className="font-medium">{r.name}</div><div className="text-[10px] font-mono text-[#5C5A55]">{r.slug}</div></td>
            <td className="p-3 uppercase text-[10px] tracking-[0.18em] font-semibold" style={{ color: GOLD }}>{r.audience}</td>
            <td className="p-3 text-xs">{r.order}</td>
            <td className="p-3"><Pill on={r.is_active}/></td>
            <td className="p-3 text-right">
              <IconBtn onClick={() => setEditing(r)} title="Edit" testid={`cat-edit-${r.id}`}><PencilSimple size={12}/></IconBtn>
              <IconBtn onClick={() => del(r.id)} title="Delete" testid={`cat-del-${r.id}`}><Trash size={12}/></IconBtn>
            </td>
          </tr>
        ))}
      </Table>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit category" : "New category"}>
          <CategoryForm data={editing} onSave={save} onCancel={() => setEditing(null)}/>
        </Modal>
      )}
    </Section>
  );
}

function CategoryForm({ data, onSave, onCancel }) {
  const [f, setF] = useState(data);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3">
      <FInput label="Name" v={f.name} on={(v) => setF({ ...f, name: v })} required testid="cat-f-name"/>
      <FSelect label="Audience" v={f.audience} on={(v) => setF({ ...f, audience: v })} testid="cat-f-audience"
               opts={[["customer","Customer"],["staff","Staff"],["official","Official"],["govt","Government"]]}/>
      <FTextarea label="Description" v={f.description || ""} on={(v) => setF({ ...f, description: v })}/>
      <div className="grid grid-cols-2 gap-3">
        <FInput label="Order" type="number" v={f.order} on={(v) => setF({ ...f, order: parseInt(v) || 0 })}/>
        <FCheck label="Active" v={f.is_active} on={(v) => setF({ ...f, is_active: v })}/>
      </div>
      <FormBar onCancel={onCancel}/>
    </form>
  );
}

// ============================================================================
// COURSES
// ============================================================================
function CoursesTab() {
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/training/courses"),
        api.get("/admin/training/categories"),
      ]);
      setRows(a.data); setCats(b.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (data.id) await api.put(`/admin/training/courses/${data.id}`, data);
      else         await api.post("/admin/training/courses", data);
      toast.success("Saved"); setEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this course with all its modules/videos/exam?")) return;
    try { await api.delete(`/admin/training/courses/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const catMap = useMemo(() => Object.fromEntries(cats.map(c => [c.id, c])), [cats]);

  return (
    <Section title="Courses" sub="Each course lives under a category. Add modules + videos after creating.">
      <Btn onClick={() => setEditing({
        name: "", description: "", category_id: cats[0]?.id || "",
        audience: cats[0]?.audience || "customer", duration_minutes: 20, is_paid: false,
        price_paise: 0, pass_threshold: 60, order: rows.length + 1, status: "draft",
      })} disabled={!cats.length} testid="ta-course-new">
        <Plus size={12}/> New course
      </Btn>
      <Table cols={["Name", "Category", "Audience", "Price", "Status", ""]}>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-[#0B2B66]/5 hover:bg-[#0B2B66]/2" data-testid={`course-${r.id}`}>
            <td className="p-3"><div className="font-medium">{r.name}</div><div className="text-[10px] text-[#5C5A55] line-clamp-1">{r.description}</div></td>
            <td className="p-3 text-xs">{catMap[r.category_id]?.name || "—"}</td>
            <td className="p-3 uppercase text-[10px] tracking-[0.18em]" style={{ color: GOLD }}>{r.audience}</td>
            <td className="p-3 text-xs">{r.is_paid ? `₹${(r.price_paise/100).toFixed(0)}` : "Free"}</td>
            <td className="p-3"><StatusPill status={r.status}/></td>
            <td className="p-3 text-right whitespace-nowrap">
              <IconBtn onClick={() => setEditing(r)} title="Edit" testid={`course-edit-${r.id}`}><PencilSimple size={12}/></IconBtn>
              <IconBtn onClick={() => del(r.id)} title="Delete" testid={`course-del-${r.id}`}><Trash size={12}/></IconBtn>
            </td>
          </tr>
        ))}
      </Table>
      {editing && (
        <Modal onClose={() => setEditing(null)} title={editing.id ? "Edit course" : "New course"}>
          <CourseForm data={editing} cats={cats} onSave={save} onCancel={() => setEditing(null)}/>
        </Modal>
      )}
    </Section>
  );
}

function CourseForm({ data, cats, onSave, onCancel }) {
  const [f, setF] = useState(data);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3">
      <FInput label="Course Name" v={f.name} on={(v) => setF({ ...f, name: v })} required testid="course-f-name"/>
      <FTextarea label="Description" v={f.description || ""} on={(v) => setF({ ...f, description: v })}/>
      <div className="grid grid-cols-2 gap-3">
        <FSelect label="Category" v={f.category_id} on={(v) => setF({ ...f, category_id: v })} testid="course-f-cat"
                 opts={cats.map(c => [c.id, `${c.name} · ${c.audience}`])}/>
        <FSelect label="Audience" v={f.audience} on={(v) => setF({ ...f, audience: v })}
                 opts={[["customer","Customer"],["staff","Staff"],["official","Official"],["govt","Government"]]}/>
        <FInput label="Duration (minutes)" type="number" v={f.duration_minutes} on={(v) => setF({ ...f, duration_minutes: parseInt(v) || 0 })}/>
        <FInput label="Pass threshold (%)" type="number" v={f.pass_threshold} on={(v) => setF({ ...f, pass_threshold: parseInt(v) || 0 })}/>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <FCheck label="Paid course" v={f.is_paid} on={(v) => setF({ ...f, is_paid: v })} testid="course-f-paid"/>
        <FInput label="Price (₹)" type="number" v={(f.price_paise || 0) / 100} on={(v) => setF({ ...f, price_paise: Math.round((parseFloat(v) || 0) * 100) })}/>
        <FInput label="Order" type="number" v={f.order} on={(v) => setF({ ...f, order: parseInt(v) || 0 })}/>
      </div>
      <FSelect label="Status" v={f.status} on={(v) => setF({ ...f, status: v })} testid="course-f-status"
               opts={[["draft","Draft"],["published","Published"]]}/>
      <FormBar onCancel={onCancel}/>
    </form>
  );
}

// ============================================================================
// MODULES & VIDEOS
// ============================================================================
function ModulesTab() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [mods, setMods] = useState([]);
  const [modEditing, setModEditing] = useState(null);
  const [vidEditing, setVidEditing] = useState(null);

  useEffect(() => { (async () => {
    try { const r = await api.get("/admin/training/courses"); setCourses(r.data); setCourseId(r.data[0]?.id || ""); }
    catch (e) { toast.error(fmtErr(e)); }
  })(); }, []);

  const load = async () => {
    if (!courseId) return;
    try { const r = await api.get(`/admin/training/modules?course_id=${courseId}`); setMods(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, [courseId]);

  const saveMod = async (d) => {
    try {
      if (d.id) await api.put(`/admin/training/modules/${d.id}`, d);
      else      await api.post("/admin/training/modules", { ...d, course_id: courseId });
      toast.success("Saved"); setModEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const delMod = async (id) => {
    if (!window.confirm("Delete this module + all its videos?")) return;
    try { await api.delete(`/admin/training/modules/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  const saveVid = async (d) => {
    try {
      if (d.id) await api.put(`/admin/training/videos/${d.id}`, d);
      else      await api.post("/admin/training/videos", d);
      toast.success("Saved"); setVidEditing(null); load();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  const delVid = async (id) => {
    if (!window.confirm("Delete this video?")) return;
    try { await api.delete(`/admin/training/videos/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Section title="Modules & Videos" sub="Each module groups 1 or more videos. Videos unlock sequentially in order.">
      <div className="mb-4">
        <FSelect label="Course" v={courseId} on={setCourseId} testid="mod-f-course" opts={courses.map(c => [c.id, c.name])}/>
      </div>
      {courseId ? (
        <>
          <Btn onClick={() => setModEditing({ name: "", description: "", order: mods.length + 1 })} testid="ta-mod-new">
            <Plus size={12}/> New module
          </Btn>
          <div className="space-y-3 mt-3">
            {mods.map((m) => (
              <div key={m.id} className="bg-white rounded-lg border border-[#0B2B66]/10 p-4" data-testid={`mod-${m.id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-heading text-base" style={{ color: BLUE }}>{m.order}. {m.name}</div>
                    <div className="text-xs text-[#5C5A55] mt-0.5">{m.description}</div>
                  </div>
                  <div className="flex items-center gap-1">
                    <IconBtn onClick={() => setVidEditing({ module_id: m.id, title: "", youtube_id: "", duration_seconds: 300, order: (m.videos || []).length + 1 })} title="Add video" testid={`mod-addvid-${m.id}`}>
                      <Plus size={12}/>
                    </IconBtn>
                    <IconBtn onClick={() => setModEditing(m)} title="Edit" testid={`mod-edit-${m.id}`}><PencilSimple size={12}/></IconBtn>
                    <IconBtn onClick={() => delMod(m.id)} title="Delete"><Trash size={12}/></IconBtn>
                  </div>
                </div>
                <div className="mt-3 space-y-1.5">
                  {(m.videos || []).length === 0 && <div className="text-[11px] text-[#8A8780] italic">No videos yet.</div>}
                  {(m.videos || []).map((v) => (
                    <div key={v.id} className="flex items-center gap-3 text-xs border border-[#0B2B66]/10 rounded-md p-2.5 bg-[#FAFBFF]" data-testid={`vid-${v.id}`}>
                      <div className="w-6 h-6 rounded-sm bg-[#0B2B66] text-white grid place-items-center text-[9px] font-mono">{v.order}</div>
                      <div className="flex-1">
                        <div className="font-medium">{v.title}</div>
                        <div className="text-[10px] font-mono text-[#5C5A55]">youtube.com/watch?v={v.youtube_id} · {Math.round(v.duration_seconds/60)}m</div>
                      </div>
                      <IconBtn onClick={() => setVidEditing(v)} title="Edit"><PencilSimple size={12}/></IconBtn>
                      <IconBtn onClick={() => delVid(v.id)} title="Delete"><Trash size={12}/></IconBtn>
                    </div>
                  ))}
                </div>
              </div>
            ))}
            {mods.length === 0 && <div className="text-sm text-[#5C5A55] italic">No modules yet — add one above to get started.</div>}
          </div>
          {modEditing && (
            <Modal onClose={() => setModEditing(null)} title={modEditing.id ? "Edit module" : "New module"}>
              <ModuleForm data={modEditing} onSave={saveMod} onCancel={() => setModEditing(null)}/>
            </Modal>
          )}
          {vidEditing && (
            <Modal onClose={() => setVidEditing(null)} title={vidEditing.id ? "Edit video" : "New video"}>
              <VideoForm data={vidEditing} onSave={saveVid} onCancel={() => setVidEditing(null)}/>
            </Modal>
          )}
        </>
      ) : <EmptyHint>Create a course first before managing modules.</EmptyHint>}
    </Section>
  );
}

function ModuleForm({ data, onSave, onCancel }) {
  const [f, setF] = useState(data);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3">
      <FInput label="Module name" v={f.name} on={(v) => setF({ ...f, name: v })} required testid="mod-f-name"/>
      <FTextarea label="Description" v={f.description || ""} on={(v) => setF({ ...f, description: v })}/>
      <FInput label="Order" type="number" v={f.order} on={(v) => setF({ ...f, order: parseInt(v) || 0 })}/>
      <FormBar onCancel={onCancel}/>
    </form>
  );
}

function VideoForm({ data, onSave, onCancel }) {
  const [f, setF] = useState(data);
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(f); }} className="space-y-3">
      <FInput label="Video title" v={f.title} on={(v) => setF({ ...f, title: v })} required testid="vid-f-title"/>
      <FInput label="YouTube URL or 11-char ID" v={f.youtube_id} on={(v) => setF({ ...f, youtube_id: v })} required placeholder="https://www.youtube.com/watch?v=..." testid="vid-f-yt"/>
      <div className="grid grid-cols-2 gap-3">
        <FInput label="Duration (seconds)" type="number" v={f.duration_seconds} on={(v) => setF({ ...f, duration_seconds: parseInt(v) || 0 })}/>
        <FInput label="Order" type="number" v={f.order} on={(v) => setF({ ...f, order: parseInt(v) || 0 })}/>
      </div>
      <div className="text-[11px] text-[#5C5A55] bg-[#FAFBFF] rounded-md p-2.5 border border-[#0B2B66]/10">
        💡 Tip: Upload your videos as <b>Unlisted</b> on YouTube — not Private — so the embed works for logged-in trainees. Keyboard controls and skip-ahead are disabled in the player.
      </div>
      <FormBar onCancel={onCancel}/>
    </form>
  );
}

// ============================================================================
// EXAMS
// ============================================================================
function ExamsTab() {
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState("");
  const [exam, setExam] = useState(null);

  useEffect(() => { (async () => {
    try { const r = await api.get("/admin/training/courses"); setCourses(r.data); setCourseId(r.data[0]?.id || ""); }
    catch (e) { toast.error(fmtErr(e)); }
  })(); }, []);

  const load = async () => {
    if (!courseId) return;
    try { const r = await api.get(`/admin/training/exam/${courseId}`); setExam(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, [courseId]);

  const save = async () => {
    try {
      await api.put(`/admin/training/exam/${courseId}`, {
        questions: exam.questions, display_count: exam.display_count,
        retake_allowed: exam.retake_allowed,
      });
      toast.success("Exam saved");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const addQ = () => setExam({ ...exam, questions: [...(exam.questions || []),
    { question: "", options: ["", "", "", ""], correct_index: 0 }] });
  const delQ = (i) => setExam({ ...exam, questions: exam.questions.filter((_, j) => j !== i) });
  const updQ = (i, patch) => setExam({ ...exam,
    questions: exam.questions.map((q, j) => j === i ? { ...q, ...patch } : q) });
  const updOpt = (i, oi, val) => updQ(i, { options: exam.questions[i].options.map((o, j) => j === oi ? val : o) });

  return (
    <Section title="Exam Bank" sub="Configure a 10-question exam per course. Trainees see 5 randomly-picked questions.">
      <div className="mb-4">
        <FSelect label="Course" v={courseId} on={setCourseId} testid="exam-f-course" opts={courses.map(c => [c.id, c.name])}/>
      </div>
      {exam ? (
        <>
          <div className="grid md:grid-cols-3 gap-4 mb-5">
            <FInput label="Questions shown per exam" type="number" v={exam.display_count || 5}
                    on={(v) => setExam({ ...exam, display_count: parseInt(v) || 5 })}/>
            <FCheck label="Allow retakes" v={!!exam.retake_allowed}
                    on={(v) => setExam({ ...exam, retake_allowed: v })}/>
            <div className="text-[11px] text-[#5C5A55] self-end">
              {(exam.questions || []).length} questions in bank
            </div>
          </div>
          <div className="space-y-4">
            {(exam.questions || []).map((q, i) => (
              <div key={i} className="bg-white rounded-lg border border-[#0B2B66]/10 p-4" data-testid={`exam-q-${i}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#5C5A55]">Question {i + 1}</div>
                  <IconBtn onClick={() => delQ(i)} title="Delete"><Trash size={12}/></IconBtn>
                </div>
                <FTextarea label="" v={q.question} on={(v) => updQ(i, { question: v })}/>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {q.options.map((o, oi) => (
                    <div key={oi} className={`flex items-center gap-2 border rounded-md p-2 transition ${q.correct_index === oi ? "border-[#2D5A27] bg-[#E8EFE6]" : "border-[#0B2B66]/15"}`}>
                      <input type="radio" name={`q-${i}`} checked={q.correct_index === oi}
                             onChange={() => updQ(i, { correct_index: oi })} className="accent-[#2D5A27]"/>
                      <input value={o} onChange={(e) => updOpt(i, oi, e.target.value)} placeholder={`Option ${oi + 1}`}
                             className="flex-1 text-sm outline-none bg-transparent"/>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2 mt-5">
            <Btn onClick={addQ} testid="exam-addq"><Plus size={12}/> Add question</Btn>
            <Btn onClick={save} primary testid="exam-save"><FloppyDisk size={12} weight="fill"/> Save exam</Btn>
          </div>
        </>
      ) : <EmptyHint>Create a course first.</EmptyHint>}
    </Section>
  );
}

// ============================================================================
// CERTIFICATE DESIGN
// ============================================================================
function DesignTab() {
  const [design, setDesign] = useState(null);

  const load = async () => {
    try { const r = await api.get("/admin/training/cert-design"); setDesign(r.data); }
    catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try { await api.put("/admin/training/cert-design", design); toast.success("Saved"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  if (!design) return <EmptyHint>Loading…</EmptyHint>;

  return (
    <Section title="Certificate Design" sub="Branding and legal text for generated certificate PDFs.">
      <div className="grid md:grid-cols-2 gap-4">
        <FInput label="Issuing Authority" v={design.issuing_authority || ""} on={(v) => setDesign({ ...design, issuing_authority: v })}/>
        <FInput label="Accreditation subtitle" v={design.accreditation_subtitle || ""} on={(v) => setDesign({ ...design, accreditation_subtitle: v })}/>
        <FInput label="Brand logo URL" v={design.brand_logo_url || ""} on={(v) => setDesign({ ...design, brand_logo_url: v })}/>
        <FInput label="Authority seal URL" v={design.authority_seal_url || ""} on={(v) => setDesign({ ...design, authority_seal_url: v })}/>
        <FInput label="Partner logo 1 URL" v={design.partner_logo_1_url || ""} on={(v) => setDesign({ ...design, partner_logo_1_url: v })}/>
        <FInput label="Partner logo 2 URL" v={design.partner_logo_2_url || ""} on={(v) => setDesign({ ...design, partner_logo_2_url: v })}/>
        <FInput label="Signature image URL" v={design.signature_image_url || ""} on={(v) => setDesign({ ...design, signature_image_url: v })}/>
        <FInput label="Signatory name" v={design.signature_name || ""} on={(v) => setDesign({ ...design, signature_name: v })}/>
        <FInput label="Signatory title" v={design.signature_title || ""} on={(v) => setDesign({ ...design, signature_title: v })}/>
        <FInput label="Cert numbering format" v={design.numbering_format || ""} on={(v) => setDesign({ ...design, numbering_format: v })} placeholder="RSG-CERT-{YYYY}-{SEQ:06}"/>
        <FInput label="QR verification base URL" v={design.qr_verification_base || ""} on={(v) => setDesign({ ...design, qr_verification_base: v })}/>
        <FInput label="Validity (years)" type="number" v={design.validity_period_years || 2} on={(v) => setDesign({ ...design, validity_period_years: parseInt(v) || 2 })}/>
        <FInput label="Primary (royal-blue)" v={design.primary_color || "#0B2B66"} on={(v) => setDesign({ ...design, primary_color: v })}/>
        <FInput label="Gold accent" v={design.gold_accent_color || "#C9A227"} on={(v) => setDesign({ ...design, gold_accent_color: v })}/>
        <FInput label="Title · Customer" v={design.title_customer || ""} on={(v) => setDesign({ ...design, title_customer: v })}/>
        <FInput label="Title · Staff" v={design.title_staff || ""} on={(v) => setDesign({ ...design, title_staff: v })}/>
        <FInput label="Title · Govt" v={design.title_govt || ""} on={(v) => setDesign({ ...design, title_govt: v })}/>
        <FInput label="Title · Partner" v={design.title_partner || ""} on={(v) => setDesign({ ...design, title_partner: v })}/>
        <FInput label="Title · Franchise" v={design.title_franchise || ""} on={(v) => setDesign({ ...design, title_franchise: v })}/>
      </div>
      <div className="mt-4">
        <FTextarea label="Legal statement (printed small under description)" v={design.legal_statement || ""}
                    on={(v) => setDesign({ ...design, legal_statement: v })} rows={3}/>
      </div>
      <div className="flex items-center gap-2 mt-5">
        <Btn onClick={save} primary testid="design-save"><FloppyDisk size={12} weight="fill"/> Save design</Btn>
      </div>
    </Section>
  );
}

// ============================================================================
// PARTNERS
// ============================================================================
function PartnersTab() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("");

  const load = async () => {
    try {
      const url = filter ? `/admin/training/partners?status=${filter}` : "/admin/training/partners";
      const r = await api.get(url); setRows(r.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, [filter]);

  const updStatus = async (id, status) => {
    try { await api.put(`/admin/training/partners/${id}/status?status=${status}`); toast.success("Updated"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <Section title="Partner Inquiries" sub="Training partnership requests from the public /training page.">
      <div className="flex items-center gap-2 mb-3">
        {["","pending","contacted","approved","rejected"].map((s) => (
          <button key={s || "all"} onClick={() => setFilter(s)} data-testid={`partner-filter-${s || "all"}`}
                  className={`h-8 px-3 rounded-md text-xs border ${filter === s ? "bg-[#0B2B66] text-white border-[#0B2B66]" : "border-[#0B2B66]/20 text-[#5C5A55] hover:bg-[#0B2B66]/5"}`}>
            {s ? s.toUpperCase() : "ALL"}
          </button>
        ))}
      </div>
      <Table cols={["Organisation", "Contact", "Interest", "Status", "Created", ""]}>
        {rows.map((r) => (
          <tr key={r.id} className="border-b border-[#0B2B66]/5" data-testid={`partner-${r.id}`}>
            <td className="p-3">
              <div className="font-medium">{r.organization}</div>
              <div className="text-[10px] text-[#5C5A55]">{r.organization_type} · {r.region || "—"}</div>
            </td>
            <td className="p-3 text-xs">
              <div>{r.name}</div>
              <div className="text-[10px] text-[#5C5A55]">{r.email} · {r.phone}</div>
            </td>
            <td className="p-3 text-xs">{r.interest_type || "—"}</td>
            <td className="p-3"><StatusPill status={r.status}/></td>
            <td className="p-3 text-[10px] text-[#5C5A55]">{(r.created_at || "").slice(0, 10)}</td>
            <td className="p-3 text-right whitespace-nowrap">
              {r.status !== "approved" && <IconBtn onClick={() => updStatus(r.id, "approved")} title="Approve" testid={`partner-approve-${r.id}`}><CheckCircle size={12}/></IconBtn>}
              {r.status !== "contacted" && <IconBtn onClick={() => updStatus(r.id, "contacted")} title="Mark contacted"><PencilSimple size={12}/></IconBtn>}
              {r.status !== "rejected" && <IconBtn onClick={() => updStatus(r.id, "rejected")} title="Reject"><X size={12}/></IconBtn>}
            </td>
          </tr>
        ))}
      </Table>
      {rows.length === 0 && <EmptyHint>No partner inquiries yet.</EmptyHint>}
    </Section>
  );
}

// ============================================================================
// PROGRESS & CERTIFICATES
// ============================================================================
function ProgressTab() {
  const [enr, setEnr] = useState([]);
  const [certs, setCerts] = useState([]);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/training/progress"),
        api.get("/admin/training/certificates"),
      ]);
      setEnr(a.data); setCerts(b.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const revoke = async (id) => {
    if (!window.confirm("Revoke this certificate? This cannot be undone.")) return;
    try { await api.post(`/admin/training/certificates/${id}/revoke`); toast.success("Revoked"); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="space-y-8">
      <Section title="Enrollments" sub={`${enr.length} total enrollments`}>
        <Table cols={["Trainee", "Course", "Status", "Progress", "Enrolled"]}>
          {enr.map((e) => (
            <tr key={e.id} className="border-b border-[#0B2B66]/5 text-xs" data-testid={`enr-${e.id}`}>
              <td className="p-3">
                <div className="font-medium">{e.trainee_email}</div>
                <div className="text-[10px] text-[#5C5A55] uppercase tracking-[0.15em]">{e.trainee_user_type}</div>
              </td>
              <td className="p-3">{e.course_name}</td>
              <td className="p-3"><StatusPill status={e.status}/></td>
              <td className="p-3">{e.progress_pct}%</td>
              <td className="p-3 text-[10px] text-[#5C5A55]">{(e.created_at || "").slice(0, 10)}</td>
            </tr>
          ))}
        </Table>
        {enr.length === 0 && <EmptyHint>No enrollments yet.</EmptyHint>}
      </Section>

      <Section title="Certificates Issued" sub={`${certs.length} total certificates`}>
        <Table cols={["Cert No", "Recipient", "Course", "Type", "Valid until", ""]}>
          {certs.map((c) => (
            <tr key={c.id} className={`border-b border-[#0B2B66]/5 text-xs ${c.revoked ? "opacity-50" : ""}`} data-testid={`cert-${c.id}`}>
              <td className="p-3 font-mono">{c.certificate_no}</td>
              <td className="p-3">{c.recipient_name}<div className="text-[10px] text-[#5C5A55]">{c.recipient_email}</div></td>
              <td className="p-3">{c.course_name || "—"}</td>
              <td className="p-3 uppercase text-[10px] tracking-[0.18em]" style={{ color: GOLD }}>{c.cert_type}</td>
              <td className="p-3">{c.valid_until}</td>
              <td className="p-3 text-right whitespace-nowrap">
                <a href={`${API}/training/certificate/${c.certificate_no}.pdf`} target="_blank" rel="noreferrer"
                   className="inline-flex items-center gap-1 text-xs text-[#0B2B66] hover:underline mr-2" data-testid={`cert-pdf-${c.id}`}>
                  PDF <ArrowRight size={9}/>
                </a>
                {!c.revoked && <IconBtn onClick={() => revoke(c.id)} title="Revoke"><X size={12}/></IconBtn>}
                {c.revoked && <span className="text-[10px] uppercase tracking-[0.2em] text-[#C25D38] font-semibold">Revoked</span>}
              </td>
            </tr>
          ))}
        </Table>
        {certs.length === 0 && <EmptyHint>No certificates issued yet.</EmptyHint>}
      </Section>
    </div>
  );
}

// ============================================================================
// Primitives
// ============================================================================
const Section = ({ title, sub, children }) => (
  <section className="bg-white rounded-xl border border-[#0B2B66]/10 p-6">
    <div className="mb-4">
      <h2 className="font-heading text-xl" style={{ color: BLUE }}>{title}</h2>
      {sub && <p className="text-xs text-[#5C5A55] mt-0.5">{sub}</p>}
    </div>
    {children}
  </section>
);

const Btn = ({ onClick, children, primary, disabled, testid }) => (
  <button type="button" onClick={onClick} disabled={disabled} data-testid={testid}
          className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-md text-xs font-medium mr-2 disabled:opacity-40 ${
            primary ? "text-white" : "border border-[#0B2B66]/20 text-[#0B2B66] hover:bg-[#0B2B66]/5"
          }`}
          style={primary ? { background: BLUE } : {}}>
    {children}
  </button>
);

const IconBtn = ({ onClick, children, title, testid }) => (
  <button type="button" onClick={onClick} title={title} data-testid={testid}
          className="inline-flex items-center justify-center w-7 h-7 rounded hover:bg-[#0B2B66]/10 text-[#5C5A55] hover:text-[#0B2B66] ml-1">
    {children}
  </button>
);

const Table = ({ cols, children }) => (
  <div className="overflow-x-auto mt-3">
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-[#0B2B66]/15 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
          {cols.map((c, i) => <th key={i} className="p-3 text-left font-semibold">{c}</th>)}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const Pill = ({ on }) => (
  <span className={`inline-block px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold ${
    on ? "bg-[#D4E0D1] text-[#1A3317]" : "bg-[#E5E2D9] text-[#5C5A55]"
  }`}>
    {on ? "Active" : "Inactive"}
  </span>
);

const StatusPill = ({ status }) => {
  const map = {
    published: ["Published", "#D4E0D1", "#1A3317"],
    draft: ["Draft", "#F9E4B7", "#7A5A11"],
    pending: ["Pending", "#F9E4B7", "#7A5A11"],
    approved: ["Approved", "#D4E0D1", "#1A3317"],
    rejected: ["Rejected", "#F4C7B9", "#7A2E18"],
    contacted: ["Contacted", "#C7DDF4", "#11427A"],
    in_progress: ["In progress", "#F9E4B7", "#7A5A11"],
    exam_ready: ["Exam ready", "#C7DDF4", "#11427A"],
    passed: ["Passed", "#D4E0D1", "#1A3317"],
    failed_attempt: ["Retry", "#F4C7B9", "#7A2E18"],
    awaiting_payment: ["Awaiting payment", "#F9E4B7", "#7A5A11"],
  };
  const [label, bg, color] = map[status] || [status, "#E5E2D9", "#5C5A55"];
  return <span className="px-2 py-0.5 rounded text-[9px] uppercase tracking-[0.15em] font-semibold" style={{ background: bg, color }}>{label}</span>;
};

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" data-testid="ta-modal">
    <div className="bg-white rounded-xl w-full max-w-xl shadow-2xl max-h-[90vh] overflow-y-auto">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#0B2B66]/10 sticky top-0 bg-white">
        <h3 className="font-heading text-lg" style={{ color: BLUE }}>{title}</h3>
        <button onClick={onClose} className="w-8 h-8 rounded grid place-items-center hover:bg-[#0B2B66]/5" data-testid="ta-modal-close"><X size={14}/></button>
      </div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

const FormBar = ({ onCancel }) => (
  <div className="flex items-center gap-2 pt-3 border-t border-[#0B2B66]/10">
    <button type="submit" data-testid="form-save"
            className="h-10 px-5 rounded-md text-white text-sm font-medium inline-flex items-center gap-2" style={{ background: BLUE }}>
      <FloppyDisk size={12} weight="fill"/> Save
    </button>
    <button type="button" onClick={onCancel} className="h-10 px-4 text-sm text-[#5C5A55] hover:text-[#0B2B66]">Cancel</button>
  </div>
);

const EmptyHint = ({ children }) => (
  <div className="text-center py-10 text-sm text-[#5C5A55]">{children}</div>
);

const FInput = ({ label, v, on, type = "text", required, placeholder, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>
    <input type={type} value={v === 0 ? 0 : (v || "")} onChange={(e) => on(e.target.value)}
           required={required} placeholder={placeholder} data-testid={testid}
           className="w-full h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66]"/>
  </div>
);

const FTextarea = ({ label, v, on, rows = 2 }) => (
  <div>
    {label && <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>}
    <textarea value={v || ""} onChange={(e) => on(e.target.value)} rows={rows}
              className="w-full px-3 py-2 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66]"/>
  </div>
);

const FSelect = ({ label, v, on, opts, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-[0.2em] font-medium text-[#5C5A55] block mb-1">{label}</label>
    <select value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testid}
            className="w-full h-10 px-3 border border-[#0B2B66]/20 rounded-md text-sm outline-none focus:border-[#0B2B66] bg-white">
      {opts.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
    </select>
  </div>
);

const FCheck = ({ label, v, on, testid }) => (
  <label className="flex items-center gap-2 text-sm cursor-pointer mt-5" data-testid={testid}>
    <input type="checkbox" checked={!!v} onChange={(e) => on(e.target.checked)} className="w-4 h-4 accent-[#0B2B66]"/>
    {label}
  </label>
);
